import { Spinner } from '@cimplico/components';
import { createContext, ReactNode, useContext } from 'react';
import ErrorSplashScreen from '../pages/ErrorSplashScreen';
import { useUserFirmRole } from '../queries/use-user-firm-role';
import { UserRoleEnum } from '../types/user';
import { useFirmContext } from './FirmProvider';
import { useUserContext } from './UserProvider';

const UserFirmRoleContext = createContext<UserRoleEnum | undefined>(undefined);

type UserFirmRoleProviderProps = {
    children: ReactNode | ReactNode[];
};

function UserFirmRoleProvider({ children }: UserFirmRoleProviderProps): JSX.Element {
    const { id } = useUserContext();
    const userFirmRoleQuery = useUserFirmRole(id);

    if (userFirmRoleQuery.isLoading) {
        return (
            <div className="absolute w-full h-[var(--remaining-screen-height)]">
                <div className="absolute top-1/3 left-1/2">
                    <Spinner />
                </div>
            </div>
        );
    }

    if (userFirmRoleQuery.isError || !userFirmRoleQuery.isSuccess) {
        if (userFirmRoleQuery.error?.response?.status === 404) {
            return (
                <ErrorSplashScreen
                    errorCode={404}
                    title="User firm role not found."
                    redirectTitle="Back to firm"
                    redirectUrl={`/`}
                    leftArrow={true}
                />
            );
        }
        return <ErrorSplashScreen title="An error has occurred." redirectTitle="Back to firm" redirectUrl={`/`} leftArrow={true} />;
    }

    return <UserFirmRoleContext.Provider value={userFirmRoleQuery.data.role}>{children}</UserFirmRoleContext.Provider>;
}

function useUserFirmRoleContext(): UserRoleEnum {
    const context = useContext(UserFirmRoleContext);
    if (context === undefined) {
        throw new Error('useUserFirmRoleContext must be used within a UserFirmRoleProvider');
    }
    return context;
}

export { UserFirmRoleProvider, useUserFirmRoleContext };
